import { useMemo, useState } from 'react';

import { ComponentDiscussion } from 'approot/activity-builder/components/component.constants';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useResponseBaseHeight } from 'approot/activity-builder/components/response/response.hooks';
import { useGetClassStudents } from '../classroom-student.hooks';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
} from '../classroom-answer.style';
import { ClassAnswerNoStudents } from '../class-answer-empty';
import DiscussionViewQuestion from 'approot/activity-builder/components/discussion/discussion-view-question';
import { getStudentDiscussionAnswers } from './discussion-response-individual.utils';
import { isRichtextValueEmpty } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import DrawResponsePreview from '../draw/draw-response-preview';
import DiscussionTextResponseReadonly from 'approot/activity-builder/components/discussion/discussion-text-response-readonly';
import { DISCUSSION_DRAW_CANVAS } from 'approot/activity-builder/components/discussion/discussion-view.constants';
import { hasDrawAnswer } from 'approot/activity-builder/components/shared/logic/has-draw-answer';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';
import { DiscussionQuestionOverride } from 'approot/activity-builder/components/discussion/discussion-view-question.style';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ActivityBuilderComponentMeasuredPreview from 'approot/activity-builder/page/activity-builder-component-sizer';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import { useActivitySheetScrollReset } from 'approot/activity-builder/components/shared/sheet/use-activity-sheet-scroll-reset';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';

type Props = {
  component: Omit<ComponentDiscussion, '__typename'>;
};

const DiscussionResponseIndividual = ({ component }: Props) => {
  const [activeStudent, setActiveStudent] = useState<number>(0);

  const { userClassId, lang } = useActivityBuilderPresentContext();

  const baseHeight = useResponseBaseHeight();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId: component.id,
    });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const studentAnswers = useMemo(
    () => data?.getClassStudentsAnswers || [],
    [data]
  );

  const results = useMemo(() => {
    const students =
      activeStudent > 0
        ? studentsInClass.filter(s => s.id === activeStudent)
        : studentsInClass;

    return getStudentDiscussionAnswers({
      studentAnswers,
      studentsInClass: students,
    });
  }, [activeStudent, studentsInClass, studentAnswers]);

  useActivitySheetScrollReset(activeStudent);

  return (
    <>
      {studentsLoading || !data ? (
        <Bouncer />
      ) : studentAnswersError || studentsError ? (
        <ErrorMessageActionable
          ariaPage="Discussion responses"
          paddingTop={0}
        />
      ) : studentsInClass.length === 0 ? (
        <ClassAnswerNoStudents />
      ) : (
        <>
          {activeStudent > 0 ? (
            <ActivitySheetInnerMax>
              <ClassroomAnswerDetailHeader
                studentId={activeStudent}
                students={studentsInClass}
                onSelect={setActiveStudent}
              />
              {isRichtextValueEmpty(results[0].textAnswer?.richtext) &&
              !hasDrawAnswer(results[0].drawAnswer) ? (
                <ClassroomAwaitingResponse />
              ) : (
                <>
                  <Box $marginBottom={24}>
                    <DiscussionQuestionOverride>
                      <DiscussionViewQuestion
                        id={component.id}
                        type={component.type}
                        discussion={component.properties.discussion}
                        style={component.properties.style}
                      />
                    </DiscussionQuestionOverride>
                  </Box>

                  {!isRichtextValueEmpty(results[0].textAnswer?.richtext) && (
                    <Box $marginBottom={24}>
                      <DiscussionTextResponseReadonly
                        answer={results[0].textAnswer?.richtext}
                        baseHeight={baseHeight}
                      />
                    </Box>
                  )}
                  {hasDrawAnswer(results[0].drawAnswer) && (
                    <DrawResponsePreview
                      id={component.id}
                      ariaLabel={`Student response for ${results[0].studentName}`}
                      dimensionsData={DISCUSSION_DRAW_CANVAS}
                      answerUpdatedAt={results[0].updatedAt}
                      answerUuid={results[0].drawAnswerUuid}
                      answer={results[0].drawAnswer}
                      language={lang}
                    />
                  )}
                </>
              )}
            </ActivitySheetInnerMax>
          ) : (
            <>
              <ActivitySheetInnerMax $center={false}>
                <Box $marginBottom={24}>
                  <DiscussionQuestionOverride>
                    <DiscussionViewQuestion
                      id={component.id}
                      type={component.type}
                      discussion={component.properties.discussion}
                      style={component.properties.style}
                    />
                  </DiscussionQuestionOverride>
                </Box>
              </ActivitySheetInnerMax>
              <Box $justifyContent="flex-end" $marginBottom={24}>
                <ClassroomAnswerStudentVisibility />
              </Box>
              <ClassroomAnswerGrid>
                {results.map(result => (
                  <ClassroomAnswerGridItem key={result.studentId}>
                    <ClassroomAnswerGridInner>
                      {isRichtextValueEmpty(result.textAnswer?.richtext) &&
                      !hasDrawAnswer(result.drawAnswer) ? (
                        <ClassroomAwaitingResponse />
                      ) : (
                        <>
                          <ClassroomAnswerGridButton
                            type="button"
                            onClick={() => setActiveStudent(result.studentId)}
                          >
                            <ScreenReaderOnly>
                              {`View ${result.studentName}'s response`}
                            </ScreenReaderOnly>
                          </ClassroomAnswerGridButton>
                          <Box style={{ width: '100%' }}>
                            {!isRichtextValueEmpty(
                              result.textAnswer?.richtext
                            ) && (
                              <ActivityBuilderComponentMeasuredPreview
                                id={result.studentId}
                                component={
                                  !isRichtextValueEmpty(
                                    result.textAnswer?.richtext
                                  ) ? (
                                    <DiscussionTextResponseReadonly
                                      answer={result.textAnswer?.richtext}
                                      baseHeight={baseHeight}
                                    />
                                  ) : null
                                }
                              />
                            )}

                            {hasDrawAnswer(result.drawAnswer) && (
                              <DrawResponsePreview
                                id={component.id}
                                ariaLabel={`Student response for ${result.studentName}`}
                                dimensionsData={DISCUSSION_DRAW_CANVAS}
                                answerUpdatedAt={result.updatedAt}
                                answerUuid={result.drawAnswerUuid}
                                answer={result.drawAnswer}
                                language={lang}
                              />
                            )}
                          </Box>
                        </>
                      )}
                    </ClassroomAnswerGridInner>
                    <ClassroomStudentIdentifierWithVisibility
                      name={result.studentName}
                      emoji={result.studentEmoji}
                    />
                  </ClassroomAnswerGridItem>
                ))}
              </ClassroomAnswerGrid>
            </>
          )}
        </>
      )}
    </>
  );
};

export default DiscussionResponseIndividual;
